import axios from 'axios'
import { makeZip } from 'client-zip'

/**
 * Build a zip of many protected files in the browser and save it.
 *
 * WPS-223: a server-built zip has to finish inside one web request, so a claim
 * with 200+ photos (a few GB) was cut off partway. Here every file is its own
 * short request to the never-expiring file-view URL, a few at a time, and the
 * zip is assembled locally - the claim's total size no longer meets any server
 * limit. A file that still fails after retries is left out and named in
 * ``NOT-INCLUDED.txt`` inside the zip, rather than costing the whole package.
 *
 * Where the browser allows it (Chrome, Edge) the zip streams straight into the
 * file the user picks, so a package of any size never sits in memory. Elsewhere
 * (Firefox, Safari) the zip is built in memory, so a large package is saved as
 * several zips of at most ``ZIP_PART_BYTES`` each, one after another: memory
 * holds one part, never the whole package.
 */

/** One file as the download manifest describes it. */
export interface ZipManifestEntry {
  uuid: string
  /** The file's name inside the zip, already unique within the package. */
  name: string
  size: number | null
  /** The file's never-expiring view URL. */
  url: string
  /**
   * Set when this file is an identical copy of one already in the package -
   * the packed file's name. Such files are left out and named in
   * ``NOT-INCLUDED.txt`` (WPS-223).
   */
  duplicate_of?: string
}

/** An identical copy left out of the package, and the file it copies. */
export interface ZipCopyLeftOut {
  name: string
  sameAs: string
}

/** The manifest's files to pack, and the identical copies to leave out. */
export function splitManifestCopies(entries: ZipManifestEntry[]): {
  files: ZipManifestEntry[]
  copies: ZipCopyLeftOut[]
} {
  const files: ZipManifestEntry[] = []
  const copies: ZipCopyLeftOut[] = []
  for (const entry of entries) {
    if (entry.duplicate_of) copies.push({ name: entry.name, sameAs: entry.duplicate_of })
    else files.push(entry)
  }
  return { files, copies }
}

/** What ``NOT-INCLUDED.txt`` says, or null when nothing was left out. */
export function notIncludedText(skipped: string[], copies: ZipCopyLeftOut[]): string | null {
  const sections: string[] = []
  if (skipped.length > 0) {
    sections.push(
      'These files could not be downloaded and are not in this zip. ' +
        'Try Download all again, or open each one from the record.\n\n' +
        `${skipped.join('\n')}\n`,
    )
  }
  if (copies.length > 0) {
    sections.push(
      'These files are identical copies of a file in this zip, so they are left out:\n\n' +
        `${copies.map((c) => `${c.name} - same as ${c.sameAs}`).join('\n')}\n`,
    )
  }
  return sections.length > 0 ? sections.join('\n') : null
}

export interface ZipDownloadProgress {
  done: number
  total: number
}

export interface ZipDownloadResult {
  saved: number
  skipped: string[]
}

/** Where the zip is written. */
export type ZipTarget =
  | { kind: 'file'; handle: FileSystemFileHandle }
  | { kind: 'blob'; fileName: string }

type SaveFilePicker = (options: {
  suggestedName: string
  types: { description: string; accept: Record<string, string[]> }[]
}) => Promise<FileSystemFileHandle>

/**
 * Ask where to save ``fileName``. Call it first thing in the click handler: the
 * save dialog needs the click's user activation. Rejects with an ``AbortError``
 * when the user cancels the dialog.
 */
export async function pickZipTarget(fileName: string): Promise<ZipTarget> {
  const showSaveFilePicker = (window as { showSaveFilePicker?: SaveFilePicker })
    .showSaveFilePicker
  if (!showSaveFilePicker) return { kind: 'blob', fileName }
  const handle = await showSaveFilePicker({
    suggestedName: fileName,
    types: [{ description: 'Zip archive', accept: { 'application/zip': ['.zip'] } }],
  })
  return { kind: 'file', handle }
}

/**
 * Delete the file the save dialog created. The browser creates it, empty, the
 * moment the user picks it, so a download that then fails would otherwise leave
 * an empty zip behind. Best effort: a browser without ``remove`` keeps it.
 */
export async function discardZipTarget(target: ZipTarget): Promise<void> {
  if (target.kind !== 'file') return
  const handle = target.handle as FileSystemFileHandle & { remove?: () => Promise<void> }
  try {
    await handle.remove?.()
  } catch {
    // Already gone, or the browser refused: nothing more to do.
  }
}

export const NOT_INCLUDED_FILE_NAME = 'NOT-INCLUDED.txt'

/**
 * Files fetched at once - what a browser opens to one host in parallel, so a
 * slow file never leaves the connection idle. Bounds browser memory and the
 * load on the API.
 */
export const ZIP_PARALLEL_FETCHES = 6
const FETCH_ATTEMPTS = 3

/** The most one zip built in memory holds; a larger package is split. */
export const ZIP_PART_BYTES = 300 * 1024 * 1024

/**
 * Consecutive groups of ``entries`` of at most ``maxBytes`` each. A file larger
 * than ``maxBytes`` gets a part of its own; an unknown size counts as nothing.
 */
export function planZipParts(
  entries: ZipManifestEntry[],
  maxBytes: number,
): ZipManifestEntry[][] {
  const parts: ZipManifestEntry[][] = []
  let current: ZipManifestEntry[] = []
  let bytes = 0
  for (const entry of entries) {
    const size = entry.size ?? 0
    if (current.length > 0 && bytes + size > maxBytes) {
      parts.push(current)
      current = []
      bytes = 0
    }
    current.push(entry)
    bytes += size
  }
  if (current.length > 0) parts.push(current)
  return parts
}

/** ``claim.zip`` as part 2 of 3: ``claim-part-2-of-3.zip``. */
export function zipPartName(fileName: string, index: number, count: number): string {
  return `${fileName.replace(/\.zip$/i, '')}-part-${index + 1}-of-${count}.zip`
}

async function fetchFile(url: string): Promise<Blob> {
  let lastError: unknown
  for (let attempt = 1; attempt <= FETCH_ATTEMPTS; attempt += 1) {
    try {
      // The bare client, not the app's ``api``: the URL carries its own access
      // token, so no session header (which would also force a CORS preflight on
      // every file). The fetch adapter: as fast as plain fetch on 220 photos,
      // where the default XHR adapter ran out of browser blob storage on repeat
      // runs.
      const response = await axios.get<Blob>(url, { responseType: 'blob', adapter: 'fetch' })
      return response.data
    } catch (err) {
      lastError = err
      // The file is gone or the link is refused - asking again will not help.
      const status = axios.isAxiosError(err) ? err.response?.status : undefined
      if (status === 403 || status === 404) break
    }
    if (attempt < FETCH_ATTEMPTS) {
      await new Promise((resolve) => setTimeout(resolve, 500 * attempt))
    }
  }
  throw lastError
}

async function* zipEntries(
  entries: ZipManifestEntry[],
  skipped: string[],
  onProgress?: (progress: ZipDownloadProgress) => void,
  copies: ZipCopyLeftOut[] = [],
) {
  const total = entries.length
  const inFlight: Promise<Blob | null>[] = []
  let next = 0
  const startNext = () => {
    const entry = entries[next]
    next += 1
    inFlight.push(
      fetchFile(entry.url).catch((err) => {
        console.warn(`Download all: could not fetch ${entry.name}`, err)
        return null
      }),
    )
  }
  while (next < total && inFlight.length < ZIP_PARALLEL_FETCHES) startNext()

  for (let index = 0; index < total; index += 1) {
    const blob = await inFlight.shift()!
    if (next < total) startNext()
    onProgress?.({ done: index + 1, total })
    if (!blob) {
      skipped.push(entries[index].name)
      continue
    }
    yield { name: entries[index].name, input: blob, lastModified: new Date() }
  }

  // Nothing to save: fail before the zip is finished, so a streamed file is
  // discarded rather than left on disk empty.
  if (total > 0 && skipped.length === total) {
    throw new Error('None of the files could be downloaded.')
  }
  const notIncluded = notIncludedText(skipped, copies)
  if (notIncluded) {
    yield { name: NOT_INCLUDED_FILE_NAME, input: notIncluded, lastModified: new Date() }
  }
}

function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  // Revoking straight away can cancel a large save before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/** Fetch every manifest entry and write them, zipped, to ``target``. */
export async function downloadFilesAsZip(
  entries: ZipManifestEntry[],
  target: ZipTarget,
  onProgress?: (progress: ZipDownloadProgress) => void,
  {
    partBytes = ZIP_PART_BYTES,
    copies = [],
  }: {
    partBytes?: number
    /** Identical copies left out, named in ``NOT-INCLUDED.txt``. */
    copies?: ZipCopyLeftOut[]
  } = {},
): Promise<ZipDownloadResult> {
  const skipped: string[] = []
  if (target.kind === 'file') {
    // pipeTo closes the file on success and aborts it - discarding the
    // partial write - if anything fails.
    try {
      await makeZip(zipEntries(entries, skipped, onProgress, copies)).pipeTo(
        await target.handle.createWritable(),
      )
    } catch (err) {
      await discardZipTarget(target)
      throw err
    }
    return { saved: entries.length - skipped.length, skipped }
  }

  // In memory: build and save one part at a time.
  const parts = planZipParts(entries, partBytes)
  let done = 0
  let savedParts = 0
  for (const [index, part] of parts.entries()) {
    const partSkipped: string[] = []
    const offset = done
    try {
      const zip = makeZip(
        zipEntries(
          part,
          partSkipped,
          (p) => onProgress?.({ done: offset + p.done, total: entries.length }),
          // The copies are named once, in the first part.
          index === 0 ? copies : [],
        ),
      )
      const name =
        parts.length === 1 ? target.fileName : zipPartName(target.fileName, index, parts.length)
      saveBlob(await new Response(zip).blob(), name)
      savedParts += 1
    } catch (err) {
      // A part none of whose files could be read is left out; the rest still save.
      if (parts.length === 1 || partSkipped.length !== part.length) throw err
    } finally {
      skipped.push(...partSkipped)
      done += part.length
    }
  }
  if (savedParts === 0) throw new Error('None of the files could be downloaded.')
  return { saved: entries.length - skipped.length, skipped }
}
